// A specular highlight that sweeps across its parent every few seconds.
//
// Used on the primary CTAs to keep a gold pill from reading as flat, static
// furniture — the sweep is what makes the button look live and invites the
// click. The parent must be `position: relative` (or absolute) with
// `overflow: 'hidden'` so the band is clipped to its shape.
//
// The band is a GRADIENT (transparent → colour → transparent), not a solid
// fill. A solid one reads as a hard-edged bar sliding across the button —
// especially on a wide, flat CTA, where it looks like a rendering artefact
// rather than light.

import React, {useEffect} from 'react';
import {StyleProp, StyleSheet, ViewStyle} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';

interface ShineProps {
  /** Width of the light band. */
  width?: number;
  /** How far it travels; defaults to a comfortable pill width. */
  travel?: number;
  /** Sweep duration. */
  duration?: number;
  /** Quiet time between sweeps. */
  interval?: number;
  /** Start offset, so several CTAs on screen don't flash in unison. */
  delay?: number;
  /** Peak colour at the centre of the band. */
  color?: string;
  /** Peak opacity. Keep low — this should read as light, not as paint. */
  intensity?: number;
  style?: StyleProp<ViewStyle>;
}

export default function Shine({
  width = 46,
  travel = 190,
  duration = 850,
  interval = 2600,
  delay = 0,
  color = '255,255,255',
  intensity = 0.45,
  style,
}: ShineProps) {
  const p = useSharedValue(0);

  useEffect(() => {
    p.value = withDelay(
      delay,
      withRepeat(
        withSequence(
          withTiming(1, {duration, easing: Easing.inOut(Easing.quad)}),
          // Snap back off-screen, then hold before the next pass.
          withTiming(0, {duration: 0}),
          withTiming(0, {duration: interval}),
        ),
        -1,
        false,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, interval, delay]);

  const bandStyle = useAnimatedStyle(() => ({
    transform: [
      {translateX: -width + p.value * (travel + width)},
      {skewX: '-14deg'},
    ],
    // Fade in and out at the edges so the band never pops in or out.
    opacity: Math.sin(p.value * Math.PI),
  }));

  return (
    <Animated.View
      pointerEvents="none"
      style={[styles.band, {width}, style, bandStyle]}>
      <LinearGradient
        colors={[
          `rgba(${color},0)`,
          `rgba(${color},${intensity})`,
          `rgba(${color},0)`,
        ]}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
        style={StyleSheet.absoluteFill}
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  band: {
    position: 'absolute',
    // Overshoot vertically so the skew doesn't expose a corner.
    top: -20,
    bottom: -20,
    left: 0,
  },
});
